import { useQuery } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import { toFilters } from '@/api/customers';
import type { ContractDirection, ContractRole, ContractRow } from '@/api/contracts';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Services — the global standalone Service manager (legacy `modules/Service`).
 * The list has four server-paginated feeds off `/services*`: All, Waiting-confirm,
 * POC and Device-liquidation, plus a `/services/statistical` count band. Field
 * names / endpoints are lifted verbatim from the v4 backend so requests hit
 * unchanged; the row shape below was verified against live staging
 * (`api-staging-datacentral`, see `scripts/probe-services.mjs`).
 */

/* ── Enums ─────────────────────────────────────────────────────────────── */

/** DB `service_status` (verified live). */
export type ServiceStatus =
  | 'active'
  | 'waiting-confirm'
  | 'trialing'
  | 'rejected'
  | 'on-resubmit-information'
  | 'cancelled'
  | 'waiting-create-service'
  | 'pending-terminated';

/* ── Types ─────────────────────────────────────────────────────────────── */

/**
 * `GET /services` row (`GetServicesRow`). Superset used by every list feed and
 * (with `employee_names` / `customer_language`) the detail record.
 */
export interface ServiceRow {
  service_id: number;
  service_name: string;
  /** Service-instance / entity name (the backend `name` column). */
  name?: string | null;
  service_taxable?: boolean;
  service_type?: string;
  service_status: ServiceStatus;
  is_poc?: boolean;
  service_template: string;
  product_group_code?: string;
  product_group_name?: string;
  servicable_type?: string;
  servicable_id?: number;
  customer_id?: number;
  contract_id?: number | null;
  contract_role?: ContractRole | null;
  contract_direction?: ContractDirection | null;
  product_id?: number;
  contact_id?: number;
  service_creatable_uuid?: string;
  service_creatable_type?: string;
  service_start_date?: string | null;
  service_stopped_date?: string | null;
  service_off_date?: string | null;
  service_generate_date?: string;
  service_statement_date?: string;
  service_price_type?: string;
  service_price_register?: number;
  service_price_recurring?: number;
  service_pricing_model?: string;
  service_tax_percentage?: number;
  service_currency?: string;
  service_exchange_rate?: number;
  employee_create_id?: number;
  employee_sale_tracking_ids?: number[];
  employee_names?: string[];
  customer_journey_id?: number;
  customer_name?: string;
  company_name?: string | null;
  customer_language?: string;
  cs_followers?: string[];
  sale_followers?: string[];
  /** POC feed only: the customer's response / retention notes. */
  service_response_poc?: string[];
  service_retention_poc?: string[];
  service_time_id?: number;
  statement_range?: number | null;
  statement_startdate?: string | null;
  statement_enddate?: string | null;
  first_startdate?: string | null;
  first_enddate?: string | null;
  current_startdate?: string | null;
  current_enddate?: string | null;
  continue_startdate?: string | null;
  continue_enddate?: string | null;
  renew_range?: number | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

/** `GET /services/statistical` row — one `count` per (is_poc, status) bucket. */
export interface ServiceStatBucket {
  is_poc: boolean;
  service_status: ServiceStatus;
  count: number;
}

/* ── Detail tab types (verified live — see scripts/probe-services.mjs) ────── */

/** `GET /services/:id` — detail record (list row + `employee_names`/`customer_language`). */
export type ServiceDetail = ServiceRow;

/** `GET /services/:id/dates` — the service-time lifecycle ranges. */
export interface ServiceDates {
  service_time_id: number;
  service_id: number;
  statement_range?: number | null;
  statement_startdate?: string | null;
  statement_enddate?: string | null;
  first_startdate?: string | null;
  first_enddate?: string | null;
  current_startdate?: string | null;
  current_enddate?: string | null;
  continue_startdate?: string | null;
  continue_enddate?: string | null;
  renew_range?: number | null;
  created_at?: string;
  updated_at?: string;
}

/** `GET /services/:id/prices` — register/recurring price history rows. */
export interface ServicePrice {
  id: number;
  service_id: number;
  service_name?: string;
  price_type: 'recurring' | 'register';
  price: number;
  effective_from: string;
  effective_to: string | null;
  source?: string;
  source_id?: number | null;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

/** `GET /services/:id/vat-rates` — VAT rate history (product-group or service level). */
export interface ServiceVatRate {
  id: number;
  source: 'product_group' | 'service';
  product_group_code?: string;
  service_id?: number;
  tax_rate: number;
  effective_from: string;
  effective_to: string | null;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

/** `GET /services/:id/technical` — technical notification records. */
export interface ServiceNotification {
  service_system_information_id: number;
  service_id: number;
  notification_name?: string;
  dns_enable?: boolean;
  dns_managements?: Array<{ name?: string; type?: string; target?: string }>;
  metadata?: Array<{ key?: string; value?: string }>;
  documentations?: Array<{ name?: string; link?: string }>;
  attachments?: Array<{ attachment_name?: string; attachment_url?: string; [k: string]: unknown }>;
  employee_name?: string | null;
  delivery_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** `GET /services/:id/technical/mailers` — a customer mailer receiver config (verified live). */
export interface ServiceMailer {
  customer_mailer_id: number;
  customer_id: number;
  customer_mailer_name: string;
  mailer_type_ids?: number[];
  mailer_receiver?: string;
  mailer_cc?: string;
  mailer_bcc?: string;
  service_ids?: number[];
  sendable?: boolean;
  created_at?: string;
}

/**
 * `GET /services/:id/technical/notifications` — one sent-email tracking record.
 * The mailer/customer/sender details are DOUBLE-nested under `metadata.metadata`
 * (verified live on svc 5771); `subject` sits on the outer `metadata`.
 */
export interface ServiceMailerTracking {
  task_id: number;
  task_jid?: string;
  task_name?: string;
  task_status?: string;
  task_created_at?: string;
  metadata?: {
    subject?: string;
    metadata?: {
      mailer?: { customer_mailer_name?: string; mailer_receiver?: string; mailer_cc?: string; mailer_bcc?: string };
      customer?: { customer_name?: string; customer_email?: string };
      sender?: { employee_name?: string; employee_email?: string };
      notification?: { notification_name?: string };
    };
    sender?: { employee_name?: string };
  };
}

/**
 * `GET /services/:id/configoptions` — one provisioning/pricing unit of the
 * service (VM package, bandwidth commitment, storage capacity, …). Verified live
 * on svc 5841 (VCloud). Deliberately partial — templates read the fields they need.
 */
export interface ServiceConfigOption {
  service_configoption_id: number;
  service_id: number;
  name: string;
  unit?: string;
  type?: string;
  brand_name?: string | null;
  model_name?: string | null;
  capacity_value?: number | null;
  capacity_unit?: string | null;
  statementable?: boolean;
  commitment_value?: number;
  commitment_time?: number;
  commitment_price_per_unit?: number;
  commitment_price_total?: number;
  commitment_prepaid?: boolean;
  commitment_startdate?: string | null;
  commitment_enddate?: string | null;
  commitment_available?: number | null;
  commitment_used?: number | null;
  warning_enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * `GET /services/:id/configoptions/:cid/price-history` — one committed-price
 * record for a config option (flat pricing). Verified live on CDN 5612.
 */
export interface ServiceConfigOptionPrice {
  id: number;
  service_configoption_id: number;
  commitment_value: number;
  commitment_price_per_unit: number;
  effective_from: string;
  effective_to?: string | null;
  source?: string;
  description?: string;
  created_at?: string;
}

/** `GET /services/:id/updates` — one service change-history / activity entry (verified live). */
export interface ServiceUpdate {
  created_at?: string;
  employee_name?: string;
  action?: string;
  description?: string;
  metadata?: Record<string, unknown> | null;
}

/** `GET /services/:id/price-change-requests` — a pending/applied price change (verified live). */
export interface ServicePriceChangeRequest {
  id: number;
  service_id: number;
  product_group_code?: string;
  old_price?: number;
  new_price?: number;
  price_type?: 'recurring' | 'register';
  effective_from?: string | null;
  status?: 'pending' | 'applied' | 'rejected';
  note?: string;
  created_at?: string;
}

/** `GET /services/:id/alerts` — usage threshold alert per config option (verified live). */
export interface ServiceAlert {
  id: string;
  service_configoption_id: number;
  name: string;
  unit?: string;
  threshold_type?: string;
  warning_threshold: number;
  critical_threshold: number;
  commitment_value?: number;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

/* ── SLA ───────────────────────────────────────────────────────────────── */

export type SlaTier = 'bronze' | 'silver' | 'gold' | 'platinum' | 'custom';
export type DowntimeType = 'unplanned' | 'planned' | 'maintenance' | 'emergency';
export type DowntimeCause = 'provider' | 'customer' | 'third_party' | 'force_majeure' | 'scheduled';

/** Per-ticket-type response/resolution targets (minutes). */
export interface SlaRule {
  ticket_type_group: string;
  ticket_type_key?: string | null;
  response_time_minutes: number;
  resolution_time_minutes: number;
  priority?: string;
}

export interface SlaBusinessHoursConfig {
  timezone?: string;
  work_days?: number[];
  work_hours?: { start?: string; end?: string };
  include_weekends?: boolean;
}

/** `GET /sla-templates/active` — the reusable Bronze/Silver/Gold/Platinum tiers. */
export interface SlaTemplate {
  sla_template_id: number;
  template_name: string;
  template_tier: SlaTier;
  template_description?: string;
  uptime_guarantee: number;
  downtime_allowance_minutes: number;
  default_response_time: number;
  default_resolution_time: number;
  business_hours_only: boolean;
}

/**
 * `GET /services/:id/sla` — the SLA config attached to a service (`result` is
 * null when none). Shape verified live on svc 5003 (Silver template): the
 * backend returns both the raw values and computed `effective_*` overrides, and
 * `template_tier` as a `{sla_tier, valid}` wrapper.
 */
export interface ServiceSla {
  service_sla_id?: number;
  service_id?: number;
  sla_template_id?: number | null;
  sla_source?: string;
  effective_from?: string | null;
  effective_to?: string | null;
  uptime_guarantee?: number | null;
  downtime_allowance_minutes?: number | null;
  business_hours_only?: boolean | null;
  business_hours_config?: SlaBusinessHoursConfig | null;
  sla_rules?: SlaRule[];
  escalation_rules?: unknown[];
  custom_terms?: { key: string; value: string }[];
  contract_id?: number | null;
  is_active?: boolean;
  sla_status?: string;
  template_name?: string;
  template_tier?: { sla_tier: SlaTier | ''; valid: boolean } | SlaTier | null;
  template_default_response_time?: number;
  template_default_resolution_time?: number;
  effective_uptime_guarantee?: number;
  effective_downtime_allowance?: number;
  effective_business_hours_only?: boolean;
  effective_sla_rules?: SlaRule[];
  [key: string]: unknown;
}

/** `GET /services/:id/sla/downtime` — one downtime incident. */
export interface SlaDowntimeLog {
  downtime_log_id: number;
  service_id: number;
  downtime_type: DowntimeType;
  downtime_cause?: DowntimeCause;
  downtime_start: string;
  downtime_end?: string | null;
  downtime_minutes?: number | null;
  downtime_description?: string;
  root_cause?: string;
  counts_against_sla: boolean;
  excluded_reason?: string;
  ticket_id?: number | null;
  status?: string;
}

/** `GET /services/:id/sla/downtime/uptime` — aggregate uptime for a window. */
export interface SlaUptimeStats {
  service_id: number;
  target_uptime: number;
  allowed_downtime: number;
  total_downtime_minutes: number;
  incident_count: number;
  actual_uptime: number;
  sla_met: boolean;
}

/** `GET /services/:id/invoices` — receipts / invoices raised for the service. */
export interface ServiceInvoice {
  invoice_id: number;
  customer_id?: number;
  invoice_status?: string;
  invoice_type?: string;
  invoice_amount?: number;
  invoice_vat?: number;
  invoice_total?: number;
  invoice_duedate?: string;
  invoice_apply_startdate?: string;
  invoice_apply_enddate?: string;
  created_at?: string;
}

/* ── Params ────────────────────────────────────────────────────────────── */

export interface ServiceListParams {
  page?: number;
  page_size?: number;
  q?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  /** Structured filters, base64-encoded into the `filters` query param. */
  filters?: Record<string, unknown>;
}

function listQuery(params: ServiceListParams): string {
  const { filters, ...rest } = params;
  const q: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== '') q[k] = v as string | number;
  }
  if (filters && Object.keys(filters).length > 0) q.filters = toFilters(filters);
  return qs.stringify(q);
}

/* ── Query keys ────────────────────────────────────────────────────────── */

export const serviceKeys = {
  all: ['services'] as const,
  list: (params: ServiceListParams) => ['services', 'list', params] as const,
  waiting: (params: ServiceListParams) => ['services', 'waiting', params] as const,
  poc: (params: ServiceListParams) => ['services', 'poc', params] as const,
  deviceLiquidation: (params: ServiceListParams) => ['services', 'device-liquidation', params] as const,
  statistical: () => ['services', 'statistical'] as const,
  detail: (id: number) => ['services', 'detail', id] as const,
  dates: (id: number) => ['services', id, 'dates'] as const,
  contract: (id: number) => ['services', id, 'contract'] as const,
  prices: (id: number) => ['services', id, 'prices'] as const,
  vatRates: (id: number) => ['services', id, 'vat-rates'] as const,
  notifications: (id: number) => ['services', id, 'technical'] as const,
  invoices: (id: number, params: ServiceListParams) => ['services', id, 'invoices', params] as const,
  configoptions: (id: number) => ['services', id, 'configoptions'] as const,
  configPriceHistory: (id: number, cid: number) => ['services', id, 'configoptions', cid, 'price-history'] as const,
  alerts: (id: number) => ['services', id, 'alerts'] as const,
  alertHistory: (id: number, params: ServiceListParams) => ['services', id, 'alerts', 'history', params] as const,
  sla: (id: number) => ['services', id, 'sla'] as const,
  slaTemplates: () => ['sla-templates', 'active'] as const,
  slaDowntime: (id: number) => ['services', id, 'sla', 'downtime'] as const,
  slaUptime: (id: number, start: string, end: string) =>
    ['services', id, 'sla', 'uptime', start, end] as const,
  updates: (id: number, params: ServiceListParams) => ['services', id, 'updates', params] as const,
  priceChangeRequests: (id: number) => ['services', id, 'price-change-requests'] as const,
  addonConfigoptions: (id: number) => ['services', id, 'addons', 'configoptions'] as const,
  metadata: (id: number) => ['services', id, 'metadata'] as const,
  mailers: (id: number) => ['services', id, 'technical', 'mailers'] as const,
  mailerTracking: (id: number) => ['services', id, 'technical', 'notifications'] as const,
  activationWorkflow: (id: number) => ['services', id, 'internal-tickets'] as const,
};

/* ── List feeds ────────────────────────────────────────────────────────── */

/** `GET /services` — the main (all) service list. */
export function useServices(params: ServiceListParams = {}) {
  return useQuery({
    queryKey: serviceKeys.list(params),
    queryFn: async (): Promise<ResponseArray<ServiceRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceRow>>(
        `/services?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /services/waiting-confirmations` — services awaiting confirmation. */
export function useWaitingConfirmServices(params: ServiceListParams = {}) {
  return useQuery({
    queryKey: serviceKeys.waiting(params),
    queryFn: async (): Promise<ResponseArray<ServiceRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceRow>>(
        `/services/waiting-confirmations?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /services/poc` — proof-of-concept / trial services. */
export function usePocServices(params: ServiceListParams = {}) {
  return useQuery({
    queryKey: serviceKeys.poc(params),
    queryFn: async (): Promise<ResponseArray<ServiceRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceRow>>(
        `/services/poc?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /services/device-liquidation` — services flagged for device liquidation. */
export function useDeviceLiquidationServices(params: ServiceListParams = {}) {
  return useQuery({
    queryKey: serviceKeys.deviceLiquidation(params),
    queryFn: async (): Promise<ResponseArray<ServiceRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceRow>>(
        `/services/device-liquidation?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/* ── Internal-ticket activation workflow ───────────────────────────────── */

export type WorkflowStepStatus = 'todo' | 'doing' | 'blocked' | 'done';

/** One step of a service's internal-ticket workflow (e.g. CS verification). */
export interface TicketWorkflowStep {
  step_id: number;
  step_name: string;
  step_order: number;
  status: WorkflowStepStatus;
  approved_at: string | null;
  approved_by: number | null;
  approver_name: string | null;
  filled_data: Record<string, unknown> | null;
  required_fields: string[] | null;
}

/** An internal ticket (e.g. "Service Activation") driving the confirm flow. */
export interface ServiceActivationTicket {
  ticket_id: number;
  ticket_status: string;
  ticket_type_title: string;
  ticket_type_key: string;
  ticket_workflow_steps: TicketWorkflowStep[];
}

/**
 * `GET /internal-tickets/services/:id` — the internal-ticket workflow attached
 * to a service (Service Activation etc.). Drives the multi-step confirm flow in
 * the Information tab: CS verification → technical provisioning → completion.
 */
export function useServiceActivationWorkflow(id: number, enabled = true) {
  return useQuery({
    queryKey: serviceKeys.activationWorkflow(id),
    queryFn: async (): Promise<ServiceActivationTicket[]> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceActivationTicket>>(
        `/internal-tickets/services/${id}`,
      );
      return res.data?.results ?? [];
    },
    enabled: enabled && !!id,
  });
}

/**
 * `GET /services/statistical` — per (is_poc, status) counts. Returns the raw
 * buckets; the stat band aggregates the `is_poc:false` slice into headline totals.
 */
export function useServiceStatistical() {
  return useQuery({
    queryKey: serviceKeys.statistical(),
    queryFn: async (): Promise<ResponseArray<ServiceStatBucket>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceStatBucket>>('/services/statistical');
      return res.data;
    },
  });
}

/* ── Detail ────────────────────────────────────────────────────────────── */

/** `GET /services/:id` — full service record. */
export function useServiceDetail(id: number) {
  return useQuery({
    queryKey: serviceKeys.detail(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<ServiceDetail>> => {
      const res = await dataCentralApi.get<ResponseObject<ServiceDetail>>(`/services/${id}`);
      return res.data;
    },
  });
}

/** `GET /services/:id/dates` — service-time lifecycle ranges. */
export function useServiceDates(id: number) {
  return useQuery({
    queryKey: serviceKeys.dates(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<ServiceDates>> => {
      const res = await dataCentralApi.get<ResponseObject<ServiceDates>>(`/services/${id}/dates`);
      return res.data;
    },
  });
}

/** `GET /services/:id/contract` — the contract(s) this service belongs to. */
export function useServiceContract(id: number) {
  return useQuery({
    queryKey: serviceKeys.contract(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ContractRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ContractRow>>(`/services/${id}/contract`);
      return res.data;
    },
  });
}

/** `GET /services/:id/prices` — register/recurring price history. */
export function useServicePrices(id: number) {
  return useQuery({
    queryKey: serviceKeys.prices(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ServicePrice>> => {
      const res = await dataCentralApi.get<ResponseArray<ServicePrice>>(`/services/${id}/prices`);
      return res.data;
    },
  });
}

/** `GET /services/:id/vat-rates` — VAT rate history. */
export function useServiceVatRates(id: number) {
  return useQuery({
    queryKey: serviceKeys.vatRates(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ServiceVatRate>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceVatRate>>(`/services/${id}/vat-rates`);
      return res.data;
    },
  });
}

/** `GET /services/:id/technical` — technical notification records. */
export function useServiceNotifications(id: number) {
  return useQuery({
    queryKey: serviceKeys.notifications(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ServiceNotification>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceNotification>>(`/services/${id}/technical`);
      return res.data;
    },
  });
}

/** `GET /services/:id/invoices` — receipts / invoices raised for the service. */
export function useServiceInvoices(id: number, params: ServiceListParams = {}) {
  return useQuery({
    queryKey: serviceKeys.invoices(id, params),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ServiceInvoice>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceInvoice>>(
        `/services/${id}/invoices?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /services/:id/configoptions` — the service's provisioning/pricing units. */
export function useServiceConfigoptions(id: number) {
  return useQuery({
    queryKey: serviceKeys.configoptions(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ServiceConfigOption>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceConfigOption>>(`/services/${id}/configoptions`);
      return res.data;
    },
  });
}

/** `GET /services/:id/configoptions/:cid/price-history` — a config option's committed-price history. */
export function useConfigOptionPriceHistory(id: number, configId: number, enabled = true) {
  return useQuery({
    queryKey: serviceKeys.configPriceHistory(id, configId),
    enabled: !!id && !!configId && enabled,
    queryFn: async (): Promise<ResponseArray<ServiceConfigOptionPrice>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceConfigOptionPrice>>(`/services/${id}/configoptions/${configId}/price-history`);
      return res.data;
    },
  });
}

/** `GET /services/:id/alerts` — usage-threshold alerts (per config option). */
export function useServiceAlerts(id: number) {
  return useQuery({
    queryKey: serviceKeys.alerts(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ServiceAlert>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceAlert>>(`/services/${id}/alerts`);
      return res.data;
    },
  });
}

/** `GET /services/:id/alerts/history` — fired-alert history (activity log). */
export function useServiceAlertHistory(id: number, params: ServiceListParams = {}) {
  return useQuery({
    queryKey: serviceKeys.alertHistory(id, params),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<Record<string, unknown>>> => {
      const res = await dataCentralApi.get<ResponseArray<Record<string, unknown>>>(
        `/services/${id}/alerts/history?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /services/:id/sla` — the SLA config attached to the service (or null). */
export function useServiceSla(id: number) {
  return useQuery({
    queryKey: serviceKeys.sla(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<ServiceSla | null>> => {
      const res = await dataCentralApi.get<ResponseObject<ServiceSla | null>>(`/services/${id}/sla`);
      return res.data;
    },
  });
}

/** `GET /sla-templates/active` — reusable SLA tiers for the apply-template picker. */
export function useActiveSlaTemplates(enabled = true) {
  return useQuery({
    queryKey: serviceKeys.slaTemplates(),
    enabled,
    queryFn: async (): Promise<SlaTemplate[]> => {
      const res = await dataCentralApi.get<ResponseArray<SlaTemplate>>('/sla-templates/active');
      return res.data?.results ?? [];
    },
  });
}

/** `GET /services/:id/sla/downtime` — downtime incident log (latest 50). */
export function useServiceDowntimeLogs(id: number, enabled = true) {
  return useQuery({
    queryKey: serviceKeys.slaDowntime(id),
    enabled: !!id && enabled,
    queryFn: async (): Promise<ResponseArray<SlaDowntimeLog>> => {
      const res = await dataCentralApi.get<ResponseArray<SlaDowntimeLog>>(
        `/services/${id}/sla/downtime?page=1&page_size=50`,
      );
      return res.data;
    },
  });
}

/**
 * `GET /services/:id/sla/downtime/uptime` — aggregate uptime for a date window.
 * The backend 404s (`record-not-found`) when the period has no downtime data;
 * that is surfaced as `null` (empty stats) rather than an error.
 */
export function useServiceUptimeStats(id: number, start: string, end: string, enabled = true) {
  return useQuery({
    queryKey: serviceKeys.slaUptime(id, start, end),
    enabled: !!id && enabled,
    retry: false,
    queryFn: async (): Promise<SlaUptimeStats | null> => {
      try {
        const res = await dataCentralApi.get<ResponseObject<SlaUptimeStats>>(
          `/services/${id}/sla/downtime/uptime?start_date=${start}&end_date=${end}`,
        );
        return res.data?.result ?? null;
      } catch (err) {
        if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
        throw err;
      }
    },
  });
}

/**
 * `GET /services/:id/metadata` — per-template service metadata (Domain registrant
 * fields, Other.Standard payment schedule, …). Shape varies by template; loosely
 * typed. `result` is null / the endpoint 404s for templates without metadata.
 */
export function useServiceMetadata(id: number, enabled = true) {
  return useQuery({
    queryKey: serviceKeys.metadata(id),
    enabled: !!id && enabled,
    retry: false,
    queryFn: async (): Promise<{ metadata?: Record<string, unknown> } | null> => {
      const res = await dataCentralApi.get<ResponseObject<{ metadata?: Record<string, unknown> } | null>>(
        `/services/${id}/metadata`,
      );
      return res.data?.result ?? null;
    },
  });
}

/** `GET /services/:id/updates` — service change-history / activity log. */
export function useServiceUpdates(id: number, params: ServiceListParams = {}) {
  return useQuery({
    queryKey: serviceKeys.updates(id, params),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ServiceUpdate>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceUpdate>>(
        `/services/${id}/updates?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /services/:id/technical/mailers` — the customer's mailer receiver configs. */
export function useServiceNotificationMailers(id: number, enabled = true) {
  return useQuery({
    queryKey: serviceKeys.mailers(id),
    enabled: enabled && !!id,
    queryFn: async (): Promise<ResponseArray<ServiceMailer>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceMailer>>(`/services/${id}/technical/mailers`);
      return res.data;
    },
  });
}

/** `GET /services/:id/technical/notifications` — sent-email tracking history. */
export function useServiceMailerTracking(id: number) {
  return useQuery({
    queryKey: serviceKeys.mailerTracking(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ServiceMailerTracking>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceMailerTracking>>(`/services/${id}/technical/notifications`);
      return res.data;
    },
  });
}

/** `GET /services/:id/price-change-requests` — pending/applied price changes. */
export function useServicePriceChangeRequests(id: number) {
  return useQuery({
    queryKey: serviceKeys.priceChangeRequests(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ServicePriceChangeRequest>> => {
      const res = await dataCentralApi.get<ResponseArray<ServicePriceChangeRequest>>(
        `/services/${id}/price-change-requests`,
      );
      return res.data;
    },
  });
}

/**
 * `GET /services/:id/addons/configoptions` — the service's addon services, each
 * with its own configoptions (for the hierarchical config table). Returns `[]`
 * when the service has no addons.
 */
export function useServiceAddonConfigoptions(id: number) {
  return useQuery({
    queryKey: serviceKeys.addonConfigoptions(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ServiceRow & { configoptions: ServiceConfigOption[] }>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceRow & { configoptions: ServiceConfigOption[] }>>(
        `/services/${id}/addons/configoptions`,
      );
      return res.data;
    },
  });
}

/* ── Statement list feed ────────────────────────────────────────────────── */

/**
 * One row in the `GET /services/statement` feed.
 * Same as `ServiceRow` plus three statement-specific fields verified live:
 * `has_active_snapshot` drives the Rollback button; `rollback_to_*` shows
 * the period a rollback would revert to.
 */
export interface ServiceStatementRow extends ServiceRow {
  has_active_snapshot: boolean;
  rollback_to_statement_startdate: string | null;
  rollback_to_statement_enddate: string | null;
}

/** `GET /services/statement` — paginated reconciliation (doi soat) feed. */
export function useStatementServices(params: ServiceListParams = {}) {
  return useQuery({
    queryKey: ['services', 'statement', params] as const,
    queryFn: async (): Promise<ResponseArray<ServiceStatementRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ServiceStatementRow>>(
        `/services/statement?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/* ── SLA Dashboard ──────────────────────────────────────────────────────── */

/** Aggregate KPI object from `GET /sla/dashboard/overview` (verified live). */
export interface SlaDashboardOverview {
  total_services_with_sla: number;
  period_minutes: number;
  total_sla_downtime_minutes: number;
  total_all_downtime_minutes: number;
  total_incidents: number;
  services_meeting_sla: number;
  services_breaching_sla: number;
  avg_uptime_percent: number;
  total_provider_downtime: number;
  total_customer_downtime: number;
  total_third_party_downtime: number;
  total_scheduled_downtime: number;
}

/** One service row in `GET /sla/dashboard/services` (verified live). */
export interface SlaDashboardServiceRow {
  service_id: number;
  service_name: string;
  customer_id: number;
  customer_name: string;
  ss_target_uptime: number;
  ss_allowed_downtime: number;
  ss_sla_downtime_minutes: number;
  ss_incident_count: number;
  period_minutes: number;
  actual_uptime: number;
  sla_met: boolean;
}

export const slaDashboardKeys = {
  overview: (start: string, end: string) => ['sla', 'dashboard', 'overview', start, end] as const,
  services: (start: string, end: string) => ['sla', 'dashboard', 'services', start, end] as const,
};

/** `GET /sla/dashboard/overview` — aggregate uptime/downtime KPIs for a date window. */
export function useSlaDashboardOverview(startDate: string, endDate: string, enabled = true) {
  return useQuery({
    queryKey: slaDashboardKeys.overview(startDate, endDate),
    enabled: enabled && !!startDate && !!endDate,
    queryFn: async (): Promise<SlaDashboardOverview> => {
      const res = await dataCentralApi.get<ResponseObject<SlaDashboardOverview>>(
        `/sla/dashboard/overview?start_date=${startDate}&end_date=${endDate}`,
      );
      return res.data.result!;
    },
  });
}

/** `GET /sla/dashboard/services` — SLA-attached services with uptime stats. */
export function useSlaDashboardServices(startDate: string, endDate: string, enabled = true) {
  return useQuery({
    queryKey: slaDashboardKeys.services(startDate, endDate),
    enabled: enabled && !!startDate && !!endDate,
    queryFn: async (): Promise<SlaDashboardServiceRow[]> => {
      const res = await dataCentralApi.get<ResponseArray<SlaDashboardServiceRow>>(
        `/sla/dashboard/services?start_date=${startDate}&end_date=${endDate}&page_size=500`,
      );
      return res.data?.results ?? [];
    },
  });
}

/* ── Full SLA Template (CRUD) ───────────────────────────────────────────── */

/**
 * Extended SLA template returned by `GET /sla-templates` and `GET /sla-templates/:id`.
 * Verified against live `/sla-templates` (full fields including escalation_rules, custom_terms).
 */
export interface SlaTemplateFull {
  sla_template_id: number;
  template_name: string;
  template_tier: SlaTier;
  template_description?: string;
  uptime_guarantee: number;
  downtime_allowance_minutes: number;
  default_response_time: number;
  default_resolution_time: number;
  business_hours_only: boolean;
  business_hours_config?: SlaBusinessHoursConfig | null;
  sla_rules?: SlaRule[];
  escalation_rules?: unknown[];
  custom_terms?: { key: string; value: string }[];
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export const slaTemplateKeys = {
  all: ['sla-templates'] as const,
  list: () => ['sla-templates', 'list'] as const,
  detail: (id: number) => ['sla-templates', id] as const,
};

/** `GET /sla-templates` — full paginated list for the management page. */
export function useSlaTemplateList() {
  return useQuery({
    queryKey: slaTemplateKeys.list(),
    queryFn: async (): Promise<SlaTemplateFull[]> => {
      const res = await dataCentralApi.get<ResponseArray<SlaTemplateFull>>(
        '/sla-templates?page_size=100',
      );
      return res.data?.results ?? [];
    },
  });
}

/** `GET /sla-templates/:id` — single template for edit pre-fill. */
export function useSlaTemplateDetail(id: number | null) {
  return useQuery({
    queryKey: slaTemplateKeys.detail(id ?? 0),
    enabled: !!id,
    queryFn: async (): Promise<SlaTemplateFull> => {
      const res = await dataCentralApi.get<ResponseObject<SlaTemplateFull>>(`/sla-templates/${id}`);
      return res.data.result!;
    },
  });
}
